/**
 * ParserEngine — runtime for the parser-CYOA stories.
 *
 * Architecture (single Convai agent, low-latency):
 *
 *   1. Player enters a room.
 *      - Curtain closes, stage cleared, backdrop swapped, props/sprites
 *        snap to home, curtain re-opens.
 *      - A fresh Convai session is opened with a per-room system prompt
 *        that bundles: room description, NPC personas, object catalog,
 *        exits, and current world state.
 *      - The agent voices the room's arrival prose.
 *   2. Player listens, then speaks (V-key).
 *      - The Convai SDK transcribes via its built-in STT; we receive
 *        the transcript through onMessage(source: 'user').
 *      - We try LOCAL intent matching first — movement, take, look,
 *        inventory, examine. These don't burn an LLM round-trip.
 *      - Anything ambiguous (NPC dialogue, asking questions) is left
 *        in the Convai conversation so the agent generates a natural
 *        response in the puppeteer's voice and TTS plays automatically.
 *   3. The agent's prose may carry effect tags
 *      <flag:name=value> / <add_item:id> / <consume:id>
 *      The engine parses them out and mutates WorldState.
 *   4. Movement closes the session and re-enters another room.
 *   5. The endingResolver runs whenever the player tries the special
 *      "__end__" exit — final ending id wraps to Game.showEnding.
 */

import { Conversation } from '@elevenlabs/client';
import type { Stage } from '../stage.ts';
import type { AudioEngine } from '../audio.ts';
import type {
  ParserWorld, Room, WorldState, ParsedAction,
} from './types.ts';
import { InventoryUI } from './inventory.ts';
import { HotspotsUI } from './hotspots.ts';
import { HeartsUI } from './hearts-ui.ts';
import { ArenaRunner } from './arena.ts';
import { track } from '../analytics.ts';
import { CHARACTER_LAYOUT, type VoiceKey } from '../script.ts';
import { PaperSprite } from '../paper-sprite.ts';

type ConvaiSession = Awaited<ReturnType<typeof Conversation.startSession>>;

export class ParserEngine {
  private state: WorldState;
  private session: ConvaiSession | null = null;
  private agentId: string | undefined;
  private inventoryUI: InventoryUI;
  private hotspotsUI: HotspotsUI;
  private heartsUI: HeartsUI;
  private subtitleEl: HTMLElement;

  // Player input loop synchronisation
  private vHeld = false;
  private playerInputResolver: ((transcript: string) => void) | null = null;
  private playerInputBuffer: string[] = [];   // received user transcripts
  private endingFired: string | null = null;
  private stopped = false;

  // NPC click-to-talk mode. When non-null, every player utterance (except
  // exit triggers) is forwarded to the agent without any local-intent
  // parsing, and every agent response is voiced via REST TTS in the
  // NPC's voiceId rather than letting Convai TTS auto-play.
  private npcMode: import('./types.ts').NPCDef | null = null;
  private npcModeBadgeEl: HTMLElement | null = null;
  // Latest in-flight narration hold promise. speakNarration awaits the
  // previous slot before starting its own, so simultaneous calls
  // (e.g. arrival prose + NPC greeting from a click) display
  // serially instead of clobbering each other's subtitles.
  private narrationInFlight: Promise<void> | null = null;
  // Bumped on interrupt. Each speakNarration captures the current
  // value at call time and re-checks after awaiting the queue — if
  // an interrupt has happened in the meantime, the queued line
  // bails before showing. Without this, pressing V mid-narration
  // would only kill the line currently held; queued lines would
  // surface the moment the current one finished.
  private narrationGeneration = 0;

  // ─── Convai connection liveness ─────────────────────────────────
  // The SDK keeps `this.session` set even after the underlying socket
  // closes (idle timeout, server kick, network blip). Writing to that
  // dead ref fires `WebSocket is already in CLOSING or CLOSED state`
  // warnings and silently drops messages. These fields mirror the
  // real liveness state from onConnect/onDisconnect/onError so we
  // can short-circuit, queue, and auto-reconnect cleanly.
  private convaiAlive = false;
  /** Cached so reconnect rebuilds a fresh prompt without bouncing
   *  through enterRoom (which would re-narrate arrival prose). */
  private convaiLastRoom: Room | null = null;
  /** Coalesces concurrent reconnect attempts — V keypress + a queued
   *  contextual update both call ensureConvaiAlive() and should share
   *  one in-flight reconnect rather than racing two open sessions. */
  private convaiReconnectInFlight: Promise<boolean> | null = null;
  /** Contextual updates queued while the socket was dead. Flushed in
   *  onConnect of the next successful session. */
  private convaiPendingContextual: string[] = [];
  /** Watchdog for "agent never replied". Started when we expect a
   *  response (V released after speaking, or contextual update sent
   *  in NPC mode); cancelled when the agent's text arrives. On
   *  expiry, a fallback narrator line plays and we mark the socket
   *  dead so the next interaction triggers a reconnect. */
  private responseWatchdogId: number | null = null;
  // Agent's turn_timeout is 2s + LLM/TTS round-trip ~3–5s = realistic
  // 5–8s total. 15s gives generous headroom and only fires when
  // generation is genuinely stuck.
  private static readonly RESPONSE_TIMEOUT_MS = 15000;

  // Active sprite-frame animation timer (e.g. the swamp frog flipping
  // between front/back cutouts). Cleared and restarted on every
  // enterRoom; null when no animation is running.
  private spriteAnimTimer: number | null = null;
  private spriteAnimUseAlt = false;

  // Visual feedback during V-hold
  private hintEl: HTMLElement | null;

  constructor(
    private world: ParserWorld,
    private stage: Stage,
    private audio: AudioEngine,
  ) {
    this.state = this.makeInitialState();
    this.agentId = (import.meta as any).env?.VITE_ELEVENLABS_AGENT_ID;
    this.inventoryUI = new InventoryUI(world.itemCatalog);
    this.hotspotsUI = new HotspotsUI(stage, (objectId) => this.onHotspotClick(objectId));
    this.heartsUI = new HeartsUI(this.state.lives);
    this.subtitleEl = document.getElementById('subtitle')!;
    this.hintEl = document.getElementById('voice-prompt-hint');
  }

  /** A hotspot was clicked — synthesize an "examine the X" utterance and
   *  feed it into the player input queue so the same code path that
   *  voice goes through handles it (local intent matcher). This keeps
   *  click and voice symmetric and authoritative. */
  private onHotspotClick(targetId: string): void {
    const room = this.world.rooms[this.state.currentRoom];

    // NPC click — id is shaped "__npc:<id>". Enter or exit NPC mode.
    if (targetId.startsWith('__npc:')) {
      const npcId = targetId.slice(6);
      const npc = room.npcs.find(n => n.id === npcId);
      if (!npc) return;
      // Toggle: clicking the same NPC again exits the mode.
      if (this.npcMode && this.npcMode.id === npc.id) {
        void this.exitNpcMode();
      } else {
        void this.enterNpcMode(npc);
      }
      return;
    }

    // Object click — synthesize an "examine the X" utterance and feed it
    // into the player input queue so voice and click share the same
    // intent path. NPC mode short-circuits this back to agent dialogue.
    const obj = room.objects.find(o => o.id === targetId);
    if (!obj) return;
    if (this.npcMode) {
      // Whisper the object reference into the conversation instead of
      // breaking character with a parser examine.
      this.handlePlayerUtterance(`What about the ${obj.name}?`);
    } else {
      this.handlePlayerUtterance(`examine the ${obj.name}`);
    }
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  /** Start the play. Resolves with the ending id once an ending fires. */
  async run(): Promise<string> {
    if (!this.agentId) {
      console.error('[parser] VITE_ELEVENLABS_AGENT_ID missing');
      return 'alone';
    }
    track('parser_started', { worldId: this.world.id });

    this.installVKey();
    this.inventoryUI.show();
    this.inventoryUI.set(this.state.inventory);
    this.hotspotsUI.show();
    this.heartsUI.setLives(this.state.lives);
    this.heartsUI.show();
    this.showHint();

    // Dev hook — call __loseLife() from devtools to trigger the
    // heart-bar swing+fall animation manually. Useful for testing
    // the lose-life flow until fight scenes wire up real damage
    // triggers via `engine.loseLife()`.
    (window as unknown as { __loseLife?: () => void }).__loseLife =
      () => this.loseLife();

    // Warm the PaperSprite image cache with every per-room override
    // and CHARACTER_LAYOUT default BEFORE the first enterRoom runs.
    // applySpriteOverrides is now sync-only when the cache is hit —
    // entering a room before the cache is populated would fall back
    // to the async path and reintroduce the witch-shows-frog race.
    // Awaiting here costs ~200-500ms at play start; worth it.
    await this.preloadAllSpriteImages();

    try {
      await this.enterRoom(this.world.startRoom);
      while (!this.stopped && !this.endingFired) {
        await this.turn();
      }
    } catch (err) {
      console.warn('[parser] loop error', err);
    } finally {
      this.removeVKey();
      this.inventoryUI.hide();
      this.hotspotsUI.clear();
      this.heartsUI.hide();
      // Stop any sprite-frame animation (e.g. the frog's flip timer).
      if (this.spriteAnimTimer !== null) {
        clearInterval(this.spriteAnimTimer);
        this.spriteAnimTimer = null;
      }
      this.hideHint();
      this.hideSubtitle();
      this.stage.setPuppeteerVisible(false);
      if (this.session) {
        try { void this.session.endSession(); } catch { /* */ }
        this.session = null;
      }
      this.convaiAlive = false;
      this.cancelResponseWatchdog();
      this.convaiPendingContextual.length = 0;
    }

    return this.endingFired ?? 'alone';
  }

  /** External stop signal (e.g., user navigates away). */
  stop() { this.stopped = true; }

  // ─── Initial state ────────────────────────────────────────────────

  private makeInitialState(): WorldState {
    return {
      currentRoom: this.world.startRoom,
      inventory: [...(this.world.startInventory ?? [])],
      flags: { ...(this.world.startFlags ?? {}) },
      visited: new Set(),
      npcMemory: {},
      cameFrom: null,
      // Three full hearts = 6 half-hearts. Decremented via loseLife()
      // which also drives the heart-bar animation.
      lives: 6,
    };
  }

  // ─── Lives / hearts ───────────────────────────────────────────────

  /** Take one life. Plays the heart-bar swing+fall animation on the
   *  rightmost remaining heart and decrements both the world state
   *  and the UI count. Public so any scene/effect/agent code can
   *  trigger a hit (e.g. failing a check, refusing the boatman).
   *
   *  When the last life falls, the game ends with a failure ending —
   *  same end-screen / fade / credits / replay flow as any other
   *  ending. The player still sees the final heart's animation play
   *  through (1s) before the ending fade kicks in. */
  loseLife() {
    if (this.state.lives <= 0) return;
    this.state.lives = Math.max(0, this.state.lives - 1);
    this.heartsUI.loseLife();
    track('player_lost_life', {
      worldId: this.world.id,
      room: this.state.currentRoom,
      remaining: this.state.lives,
    });
    console.log(`[parser] life lost → ${this.state.lives} remaining`);
    if (this.state.lives === 0) {
      // Hold for the heart's swing+fall animation (1s) so the player
      // sees the last half-heart fall before the curtain closes.
      setTimeout(() => this.fireEnding('failure'), 1100);
    }
  }

  /** End the game with the given ending id. Sets endingFired (which
   *  the run() loop watches) and unblocks every place the run loop
   *  could be waiting. Without all four of these signals, the loop
   *  was getting stuck mid-enterRoom (REST TTS in flight) or mid-turn
   *  (input awaited but no future input ever coming) and never
   *  reaching the while-condition that would let it exit. */
  private fireEnding(ending: string) {
    if (this.endingFired) return;
    this.endingFired = ending;
    // stopped is the OR'd-in second loop guard — having both set
    // means the very next while-condition check exits, no matter
    // which branch updated state last.
    this.stopped = true;
    track('parser_ending_fired', { worldId: this.world.id, ending });
    console.log(`[parser] ending fired: ${ending} (stopped=true, interrupting narration, resolving input)`);
    // Cancel any in-flight REST narration. If enterRoom is mid-
    // speakNarration when the ending fires (likely, since the test
    // damage timer kicks in before the start_room finishes its
    // arrival prose), this is what lets enterRoom return. Without
    // it the loop never gets to iterate and check endingFired.
    try { this.interruptNarration(); } catch { /* */ }
    // Resolve any pending player-input await so turn() returns.
    if (this.playerInputResolver) {
      const r = this.playerInputResolver;
      this.playerInputResolver = null;
      r('');
    }
    // Prime the input buffer in case the resolver isn't set yet
    // (turn() hasn't called waitForPlayerInput at the moment the
    // ending fires). The next call to waitForPlayerInput will pop
    // the empty string immediately and return — and turn()'s
    // `if (!transcript) return` will short-circuit it.
    this.playerInputBuffer.push('');
  }

  /** Read current life count. */
  get lives(): number { return this.state.lives; }

  // ─── Room transitions ────────────────────────────────────────────

  private async enterRoom(roomId: string): Promise<void> {
    const room = this.world.rooms[roomId];
    if (!room) {
      console.error(`[parser] unknown room id: ${roomId}`);
      return;
    }

    // Real-time arena rooms hand off to ArenaRunner instead of going
    // through the normal Convai + narration + hotspot pipeline.
    if (room.arena) {
      await this.runArenaRoom(room);
      return;
    }

    // Hide any stale subtitle text from the previous room before we
    // start the transition, so it doesn't briefly read on top of the
    // new scene's narration.
    this.hideSubtitle();

    // Tear down any session for the previous room. The new session is
    // opened once we know the arrival prose (it goes in as firstMessage
    // so Convai voices it directly).
    if (this.session) {
      try { await this.session.endSession(); } catch { /* */ }
      this.session = null;
    }
    this.convaiAlive = false;
    this.cancelResponseWatchdog();
    this.convaiPendingContextual.length = 0;

    const wasFirstVisit = !this.state.visited.has(roomId);
    const previous = this.state.currentRoom;
    this.state.currentRoom = roomId;
    this.state.cameFrom = previous;
    this.state.visited.add(roomId);

    track('parser_room_entered', { worldId: this.world.id, room: roomId, firstVisit: wasFirstVisit });

    // Curtain swap → stage rebuild → curtain rise.
    if (this.stage.isCurtainOpen()) {
      void this.audio.playSfx('curtain_close', { volume: 0.7 });
      await this.stage.curtainClose();
    }
    this.stage.clearStage();
    this.stage.setBackdrop(room.backdrop);
    if (room.music === null) this.audio.stopMusic();
    else if (room.music !== undefined) this.audio.startMusic(room.music, 0.35);
    for (const propKey of room.props) {
      this.stage.prop(propKey)?.snapVisible();
    }

    // Indoor / outdoor toggle for the dangling paper clouds. Cloud
    // sticks drifting through the witch's hut don't make narrative
    // sense; outdoor scenes get them back.
    const ceilingProps = document.getElementById('ceiling-props');
    if (ceilingProps) {
      if (room.interior) {
        ceilingProps.classList.remove('visible');
        ceilingProps.classList.add('hidden');
      } else {
        ceilingProps.classList.remove('hidden');
        // Force reflow so the visible-class fade-in transition fires.
        void ceilingProps.offsetWidth;
        ceilingProps.classList.add('visible');
      }
    }

    // Apply per-sprite image overrides BEFORE snapping sprites visible.
    // For every sprite the room shows, either swap to the room's
    // declared override art or restore the CHARACTER_LAYOUT default.
    // This ensures a previous room's override (e.g. swamp's frog
    // cutout) doesn't bleed into a later room (e.g. forest's crone
    // should reappear as the angel banner).
    this.applySpriteOverrides(room);

    for (const entry of room.spritesOnEntry) {
      const key = typeof entry === 'string' ? entry : entry.key;
      const x = typeof entry === 'string' ? undefined : entry.x;
      const y = typeof entry === 'string' ? undefined : entry.y;
      this.stage.sprite(key)?.snapVisible(x, y);
    }

    // snapVisible resets mesh.scale to (1,1,1). Re-apply the per-room
    // geometry scales now so the witch / frog quads are properly
    // narrowed to portrait — not the original banner shape.
    this.applySpriteScales(room);

    // Stop any sprite-frame animation from the previous room and start
    // the new room's, if any.
    this.restartSpriteAnim(room);
    await wait(150);
    void this.audio.playSfx('curtain_open', { volume: 0.7 });
    await this.stage.curtainOpen();

    // Refresh on-stage hotspots — invisible clickables pinned over the
    // props that visually represent interactable objects in this room.
    this.refreshHotspots(room);

    const prose = wasFirstVisit
      ? room.arrivalProse
      : (room.returnProse ?? room.arrivalProse);

    // Open Convai with the arrival prose passed as `firstMessage`. The
    // agent voices it on connect via its own TTS pipeline — runtime
    // never needs an ElevenLabs API key, only the public agent ID.
    // Show the subtitle in parallel so the player reads as the agent
    // speaks; we don't await an audio promise because Convai handles
    // the playback itself.
    this.showSubtitle(prose);
    await this.openSessionForRoom(room, prose);
  }

  /** Voice arbitrary narrator prose by calling the ElevenLabs TTS REST
   *  endpoint directly. Falls back to subtitle-only + estimated wait if
   *  the API key is missing or the request fails — never blocks the
   *  game loop on a network error. Music ducks while playing.
   *
   *  Voice ID is the same Drew preset used for the puppeteer in
   *  `voices.config.ts`, so the narrator sounds consistent across the
   *  pre-rendered (legacy) and parser-driven (current) story routes. */
  /** Public narration entry-point. Two responsibilities on top of the
   *  underlying `doSpeakNarration` fetch+play:
   *
   *   1. SERIAL QUEUE. We chain every call onto a single in-flight
   *      promise so two overlapping calls (e.g. narrator finishing the
   *      arrival prose while the player clicks an NPC) never produce
   *      two simultaneous `<Audio>` elements. Each call awaits the
   *      previous one, then runs.
   *
   *   2. NPC-MODE NARRATOR GUARD. If the player is currently locked
   *      into a conversation with an NPC and no override voice is
   *      supplied, the call is dropped — the narrator must "shut up"
   *      for the entire duration of the conversation. Calls WITH an
   *      override (the NPC's own voiceId) still play; that's how the
   *      NPC speaks. */
  private speakNarration(text: string, overrideVoiceId?: string): Promise<void> {
    // NPC-mode guard. The narrator's voice (no override) must never
    // surface while the player is talking to an NPC — even if some
    // path I haven't anticipated triggers it.
    if (this.npcMode && !overrideVoiceId) {
      console.log('[parser] suppressed narrator line while NPC mode active');
      return Promise.resolve();
    }
    // ── SYNCHRONOUS QUEUE REGISTRATION ──────────────────────────
    // Assign narrationInFlight BEFORE returning to the caller. If
    // we did this after an `await prev`, applyEffects (which runs
    // sync after handleAgentResponse calls void speakNarration)
    // would observe narrationInFlight === null and any auto-trigger
    // (autoEnterArena, autoCrossRiver) that awaits it would not
    // wait at all — racing into a curtain swap that cuts off the
    // line still being voiced.
    const myGeneration = this.narrationGeneration;
    const prev = this.narrationInFlight ?? Promise.resolve();
    const myTurn: Promise<void> = (async () => {
      try { await prev; } catch { /* prior failed or was interrupted */ }
      if (myGeneration !== this.narrationGeneration) {
        console.log('[parser] narration cancelled before play (interrupt during queue wait)');
        return;
      }
      await this.doSpeakNarration(text, overrideVoiceId);
    })();
    this.narrationInFlight = myTurn.finally(() => {
      // Only clear the slot if no newer call has replaced it.
      if (this.narrationInFlight === myTurn) this.narrationInFlight = null;
    });
    return this.narrationInFlight;
  }

  /** Cancel any in-flight subtitle hold and queued lines. Called when
   *  the player presses V — they want to speak, not wait for us to
   *  finish. Bumping narrationGeneration invalidates the awaiting
   *  hold timer in doSpeakNarration so the caption clears immediately
   *  and any queued lines are dropped. (There is no audio element to
   *  pause — narration audio is voiced by the Convai agent itself.) */
  private interruptNarration() {
    this.narrationGeneration++;
    this.hideSubtitle();
    console.log('[parser] narration interrupted');
  }

  /** Always run via `speakNarration` so the serial queue and NPC-mode
   *  guard apply.
   *
   *  RUNTIME = AGENT-ID ONLY. Narration audio comes exclusively from
   *  the Convai agent's TTS pipeline (room arrival prose is passed as
   *  `firstMessage` on session open; everything else is voiced by the
   *  agent in response to user input). This method is the SUBTITLE-
   *  ONLY fallback — it shows the caption, holds for an estimate based
   *  on text length, then hides. No ElevenLabs REST call, no API key,
   *  no /api/tts proxy needed at runtime. */
  private async doSpeakNarration(text: string, _overrideVoiceId?: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    console.log(`[parser] subtitle (${trimmed.length} chars): ${trimmed.slice(0, 80)}…`);
    this.showSubtitle(trimmed);
    // Hold time roughly tracks reading speed (~14 chars/sec) so the
    // caption stays up long enough to read. interruptNarration() bumps
    // narrationGeneration to cancel queued lines mid-wait when the
    // player presses V.
    const myGeneration = this.narrationGeneration;
    const holdMs = Math.max(2500, (trimmed.length / 14) * 1000);
    await wait(holdMs);
    if (myGeneration === this.narrationGeneration) {
      this.hideSubtitle();
    }
  }

  // ─── Convai session per room ──────────────────────────────────────

  private async openSessionForRoom(room: Room, firstMessage = ''): Promise<void> {
    const prompt = this.buildRoomPrompt(room);
    const agentId = this.agentId!;
    console.log(`[parser] opening Convai session for room "${room.id}"`);
    return new Promise((resolve, reject) => {
      Conversation.startSession({
        agentId,
        connectionType: 'websocket',
        overrides: {
          agent: {
            prompt: { prompt },
            // The arrival prose is passed in as `firstMessage` so the
            // agent voices it on connect through its own TTS pipeline —
            // runtime needs ONLY the agent ID, no ElevenLabs API key
            // ever leaves the dashboard. Empty string passed by callers
            // that don't want any greeting; the dashboard default
            // ("Welcome to the theater.") is never used because this
            // override is always present.
            firstMessage,
          },
        },
        onConnect: () => {
          console.log('[parser] convai connected');
          this.convaiAlive = true;
          // Mute mic by default — it only opens while V is held.
          try { this.session?.setMicMuted(true); } catch { /* */ }
          // Flush any contextual updates queued while we were down.
          if (this.convaiPendingContextual.length) {
            const flushed = this.convaiPendingContextual.splice(0);
            for (const m of flushed) {
              try { this.session?.sendContextualUpdate(m); }
              catch (err) { console.warn('[parser] flush sendContextualUpdate failed', err); }
            }
          }
        },
        onDisconnect: () => {
          console.log('[parser] convai disconnected');
          this.convaiAlive = false;
          // Drop the dead ref so subsequent `?.` calls no-op instead
          // of poking a closed socket. The next ensureConvaiAlive()
          // call will spin up a fresh session for the current room.
          this.session = null;
          this.cancelResponseWatchdog();
        },
        onError: (msg, ctx) => {
          console.warn('[parser] convai error', msg, ctx);
          // Errors after-the-fact (mid-stream protocol error) aren't
          // always followed by an explicit onDisconnect. Mark dead so
          // the next send/mic toggle triggers a reconnect.
          this.convaiAlive = false;
        },
        onMessage: ({ source, message }) => {
          if (typeof message !== 'string' || !message.trim()) return;
          if (source === 'user') {
            console.log(`[parser] user utterance: "${message.trim().slice(0, 80)}"`);
            // Got a transcript — refresh the watchdog. We're now
            // waiting on the agent's reply, not the transcription.
            this.startResponseWatchdog();
            this.handlePlayerUtterance(message.trim());
          } else {
            console.log(`[parser] agent response: "${message.trim().slice(0, 80)}"`);
            this.cancelResponseWatchdog();
            this.handleAgentResponse(message);
          }
        },
      })
        .then((s) => {
          this.session = s;
          this.convaiAlive = true;
          this.convaiLastRoom = room;
          try { s.setMicMuted(true); } catch { /* */ }
          resolve();
        })
        .catch((err) => {
          console.warn('[parser] startSession failed', err);
          this.convaiAlive = false;
          reject(err);
        });
    });
  }

  // ─── Convai liveness helpers ─────────────────────────────────────

  /** Ensure the Convai session is alive. If the socket is dead (idle
   *  timeout, server kick, network blip), tear down the corpse and
   *  open a fresh session for the current room. Concurrent callers
   *  share a single in-flight reconnect via convaiReconnectInFlight.
   *  Returns true iff a working session exists by resolution time. */
  private ensureConvaiAlive(): Promise<boolean> {
    if (this.convaiAlive && this.session) return Promise.resolve(true);
    if (this.stopped) return Promise.resolve(false);
    if (!this.convaiLastRoom) return Promise.resolve(false);
    if (this.convaiReconnectInFlight) return this.convaiReconnectInFlight;
    this.convaiReconnectInFlight = (async () => {
      console.log('[parser] convai reconnect — opening fresh session');
      // Close any half-dead session ref before opening a new one.
      const dead = this.session;
      this.session = null;
      if (dead) {
        try { await dead.endSession(); } catch { /* */ }
      }
      const room =
        this.world.rooms[this.state.currentRoom] ?? this.convaiLastRoom!;
      try {
        await this.openSessionForRoom(room);
      } catch (err) {
        console.warn('[parser] convai reconnect failed', err);
        this.convaiAlive = false;
      }
      // If we reconnected mid-NPC-mode, the persona context lived on
      // the previous (now dead) socket. Re-send it so the agent knows
      // to keep voicing in-character.
      if (this.convaiAlive && this.npcMode) {
        this.safeSendContextual(this.buildNpcEnterContext(this.npcMode));
      }
      return this.convaiAlive;
    })().finally(() => {
      this.convaiReconnectInFlight = null;
    });
    return this.convaiReconnectInFlight;
  }

  /** Send a contextual update with kill-safe fallbacks: if the socket
   *  is dead the message is queued and flushed in the next onConnect.
   *  Concurrent reconnect is fired-and-forgotten — caller doesn't
   *  block. */
  private safeSendContextual(msg: string): void {
    if (this.convaiAlive && this.session) {
      try {
        this.session.sendContextualUpdate(msg);
        return;
      } catch (err) {
        console.warn('[parser] sendContextualUpdate failed; queueing', err);
        this.convaiAlive = false;
      }
    }
    this.convaiPendingContextual.push(msg);
    void this.ensureConvaiAlive();
  }

  /** Toggle the SDK's mic gate without blowing up on a dead socket.
   *  Returns true iff the call landed; callers can choose to retry
   *  after ensureConvaiAlive() if they care. */
  private safeSetMicMuted(muted: boolean): boolean {
    if (!this.convaiAlive || !this.session) return false;
    try {
      this.session.setMicMuted(muted);
      return true;
    } catch (err) {
      console.warn('[parser] setMicMuted failed', err);
      this.convaiAlive = false;
      return false;
    }
  }

  /** Build the NPC-mode contextual update so it can be re-sent on
   *  reconnect. Mirrors the string used in enterNpcMode. */
  private buildNpcEnterContext(npc: import('./types.ts').NPCDef): string {
    return (
      `[mode change] The player has stepped close to ${npc.name} and is now speaking directly to them. ` +
      `Voice every reply AS ${npc.name}, in the persona already in your prompt. Keep replies to 1–2 short sentences. ` +
      `Stay in character — do NOT narrate the room or comment as the puppeteer. ` +
      `The player will say "back" or "leave" when they're done; until then assume every utterance is dialogue.`
    );
  }

  private startResponseWatchdog(): void {
    this.cancelResponseWatchdog();
    this.responseWatchdogId = window.setTimeout(() => {
      this.responseWatchdogId = null;
      console.warn('[parser] convai response timeout — surfacing fallback');
      // Just play a soft fallback line and DO NOT tear down the
      // session. Earlier we used to mark the socket dead and
      // reconnect, but that was killing valid in-flight replies and
      // leaving the old session's audio worklet bound to a closing
      // socket — which caused the "WebSocket is already in CLOSING"
      // warning storm. If the agent really IS stuck, the next
      // contextual send / V press will detect a dead socket via the
      // safe* helpers and reconnect cleanly then.
      void this.speakNarration('(the puppeteer pauses, distracted. try again.)');
    }, ParserEngine.RESPONSE_TIMEOUT_MS);
  }
  private cancelResponseWatchdog(): void {
    if (this.responseWatchdogId !== null) {
      clearTimeout(this.responseWatchdogId);
      this.responseWatchdogId = null;
    }
  }

  private buildRoomPrompt(room: Room): string {
    const objects = room.objects
      .filter(o => !o.hidden)
      .map(o => `- ${o.name}${o.aliases?.length ? ` (also: ${o.aliases.join(', ')})` : ''}: ${
        typeof o.examineProse === 'string' ? o.examineProse : '(state-dependent)'
      }`)
      .join('\n');

    const npcs = room.npcs.map(n =>
      `- ${n.name} (id: ${n.id}${n.aliases?.length ? `, also: ${n.aliases.join(', ')}` : ''}):\n  PERSONA: ${n.persona}\n  GREETING (use on first address): "${n.greeting}"`
    ).join('\n');

    const exits = room.exits.map(e =>
      `- "${e.phrases.join('" / "')}"${e.available ? ' (gated by world state)' : ''}: leads to "${e.to}"`
    ).join('\n');

    const inv = this.state.inventory.length ? this.state.inventory.join(', ') : '(empty)';
    const flags = JSON.stringify(this.state.flags);

    return [
      'You are the narrator of "The Hollow Hours", a slow paper-theater adventure played as a parser-CYOA.',
      'Speak in the puppeteer\'s voice — weary, warm, intimate, theatrical when it slips through. Short sentences.',
      'Keep every response to 1–3 sentences unless explicitly asked for more.',
      '',
      `CURRENT ROOM: ${room.id}`,
      `(arrival prose, already spoken on entry): "${room.arrivalProse}"`,
      '',
      'OBJECTS HERE (player can examine):',
      objects || '  (none)',
      '',
      'NPCS HERE (player can address):',
      npcs || '  (none)',
      '',
      'EXITS (movement is HANDLED EXCLUSIVELY BY THE ENGINE):',
      exits || '  (none)',
      '',
      `PLAYER STATE: inventory=${inv}; flags=${flags}`,
      '',
      'PROTOCOL:',
      '- The engine pre-handles ALL movement, take, look, inventory, and examine commands. If you receive a player message, it means the engine could NOT match it to any of those — so the player did NOT move and you must NOT pretend they did. Never write "we go", "we walk", "into the trees we go", "you arrive at" — those are LIES if the engine sent you the message, because the engine already decided not to move them.',
      '- If the player\'s message sounds like they wanted to go somewhere but it was ambiguous, ask them which way (list the exits in plain language) instead of inventing a transition.',
      '- When voicing an NPC, attribute clearly: e.g. \'The boatman says, "Coin for passage."\'',
      '- When the player addresses an NPC about a transaction (giving an item, accepting an offer), embed effect tags on a NEW line at the very END of your response:',
      '    <flag:name=true>      — set a world flag',
      '    <add_item:item_id>    — give the player an item',
      '    <consume:item_id>     — remove an item from the player\'s pack',
      '- Effect tags are machine-read; never speak them aloud.',
      '- The arrival prose was already narrated to the player by another voice (the engine\'s TTS). Do NOT re-speak the arrival prose; assume the player has heard it. Pick up from there if they speak.',
      '- If the engine sends "PLAYER_LEFT", say nothing.',
      '- Never invent items, exits, or NPCs that aren\'t in the lists above.',
    ].join('\n');
  }

  // ─── Player input loop ───────────────────────────────────────────

  private async turn(): Promise<void> {
    // Bail early if the run has been ended — no point waiting on
    // input that will never come.
    if (this.endingFired || this.stopped) return;
    const transcript = await this.waitForPlayerInput();
    // Re-check after the await: an ending may have fired while we
    // were waiting. Without this, an empty transcript followed by
    // an NPC-mode 2s wait could delay the exit unnecessarily.
    if (this.endingFired || this.stopped) return;
    if (!transcript) return;
    track('parser_turn', { worldId: this.world.id, room: this.state.currentRoom, transcript: transcript.slice(0, 80) });

    // ── NPC click-to-talk mode ─────────────────────────────────────
    // While locked onto an NPC, every utterance is dialogue UNLESS:
    //   1. it matches an explicit exit-mode trigger
    //      ("back", "leave", "stop", "goodbye", "nevermind",
    //      "enough"). → exit, no further action.
    //   2. it matches a phrase for one of the room's exits (e.g.
    //      "enter the house" while talking to the frog → witchHouse).
    //      → exit NPC mode AND immediately execute the go. The
    //      player's intent was clearly to move, not to chat.
    // Otherwise: hand it to the agent (mic was already streaming).
    if (this.npcMode) {
      const trimmed = transcript.trim();
      if (NPC_MODE_EXIT.test(trimmed)) {
        await this.exitNpcMode();
        return;
      }
      const exitTo = matchRoomExit(trimmed.toLowerCase().replace(/[.!?,]/g, ' ').replace(/\s+/g, ' '),
        this.world.rooms[this.state.currentRoom]);
      if (exitTo) {
        console.log(`[parser] NPC-mode exit-phrase escape → ${exitTo}`);
        await this.exitNpcMode();
        await this.executeLocalAction({ kind: 'go', to: exitTo });
        return;
      }
      // Player audio already streamed to the agent during their V-hold;
      // the agent's response will arrive through onMessage and be
      // voiced by handleAgentResponse in the NPC's voice.
      await wait(2000);
      return;
    }

    // Local intent — no LLM round-trip.
    const local = this.parseLocalIntent(transcript);
    if (local) {
      await this.executeLocalAction(local);
      return;
    }
    // Otherwise: the player's audio was already streamed to the agent
    // via Convai during their V hold; the agent's response arrives
    // through onMessage(source != 'user'), handled in handleAgentResponse.
    // Tiny tail so the analytics_turn event doesn't fire mid-syllable
    // on the agent's last word; V keydown is not blocked by this since
    // the keypress handler is a direct event listener, not part of
    // turn().
    await wait(200);
  }

  // ─── NPC click-to-talk mode ───────────────────────────────────────

  /** Enter NPC click-to-talk mode for the given character.
   *
   *  - Tells the agent (via sendContextualUpdate) that subsequent
   *    player utterances are direct addresses to this NPC, and
   *    instructs it to keep replies short and in-character. The
   *    agent's own TTS voices everything in Drew's voice — runtime
   *    no longer uses per-NPC REST TTS, so the audible "mask swap"
   *    is gone but the persona-driven dialogue still reads.
   *  - Shows the NPC greeting as a subtitle while the agent picks up
   *    the conversation thread.
   *  - Pins a "speaking to {name}" badge to the screen until exit. */
  private async enterNpcMode(npc: import('./types.ts').NPCDef): Promise<void> {
    if (this.npcMode) return;
    this.npcMode = npc;
    this.showNpcBadge(npc.name);
    track('npc_mode_entered', { worldId: this.world.id, room: this.state.currentRoom, npc: npc.id });

    // Hand the agent a contextual update so it knows to voice the next
    // exchange as this NPC. The persona text from the room prompt is
    // already in scope; this just biases the agent toward staying in
    // character for the upcoming player turns. safeSendContextual
    // handles a dead socket by queueing the message + reconnecting.
    this.safeSendContextual(this.buildNpcEnterContext(npc));

    // Show the authored greeting as a subtitle. The agent will pick up
    // the conversation in-character on the player's next utterance.
    await this.speakNarration(npc.greeting);
  }

  private async exitNpcMode(): Promise<void> {
    const npc = this.npcMode;
    if (!npc) return;
    this.npcMode = null;
    this.hideNpcBadge();
    track('npc_mode_exited', { worldId: this.world.id, room: this.state.currentRoom, npc: npc.id });

    this.safeSendContextual(
      `[mode change] The player has stepped back from ${npc.name}. Resume normal narration. ` +
      `If they speak next, you're the puppeteer-narrator again unless they explicitly address an NPC.`
    );
  }

  private showNpcBadge(name: string) {
    if (!this.npcModeBadgeEl) {
      const el = document.createElement('div');
      el.id = 'npc-mode-badge';
      el.className = 'npc-mode-badge';
      document.body.appendChild(el);
      this.npcModeBadgeEl = el;
    }
    this.npcModeBadgeEl.innerHTML =
      `<span class="npc-mode-badge-label">speaking to <em>${this.escapeHtml(name)}</em></span>` +
      `<span class="npc-mode-badge-hint">say "back" or click them again to step away</span>`;
    this.npcModeBadgeEl.classList.remove('hidden');
    void this.npcModeBadgeEl.offsetWidth;
    this.npcModeBadgeEl.classList.add('visible');
  }
  private hideNpcBadge() {
    if (!this.npcModeBadgeEl) return;
    this.npcModeBadgeEl.classList.remove('visible');
    setTimeout(() => this.npcModeBadgeEl?.classList.add('hidden'), 280);
  }
  private escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  private waitForPlayerInput(): Promise<string> {
    if (this.playerInputBuffer.length > 0) {
      return Promise.resolve(this.playerInputBuffer.shift()!);
    }
    return new Promise((resolve) => { this.playerInputResolver = resolve; });
  }

  private handlePlayerUtterance(text: string) {
    if (this.playerInputResolver) {
      const r = this.playerInputResolver;
      this.playerInputResolver = null;
      r(text);
    } else {
      this.playerInputBuffer.push(text);
    }
  }

  // ─── Local intent matcher (cheap, no LLM) ────────────────────────

  private parseLocalIntent(raw: string): ParsedAction | null {
    const t = raw.toLowerCase().trim().replace(/[.!?,]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!t) return null;
    const room = this.world.rooms[this.state.currentRoom];
    const tokens = new Set(t.split(' ').filter(Boolean));

    // ── Always-allowed locals (work even when phrased as questions) ───
    // INVENTORY
    if (/^(inventory|inv|what (do i have|am i carrying)|pack|bag)$/.test(t)) {
      return { kind: 'inventory' };
    }
    // LOOK around
    if (/^(look|look around|where am i|describe|describe (the )?(room|surroundings|scene))$/.test(t)) {
      return { kind: 'look' };
    }

    // ── Question gate ────────────────────────────────────────────────
    // If the player is ASKING something rather than COMMANDING it, hand
    // it to the agent. This prevents the engine from misreading inputs
    // like "where can the child go" as a "go" command (the word "go"
    // would otherwise trigger the motion-verb fallback or a lenient
    // exit-phrase match) or "is the river dangerous" as a navigation
    // command to the river. The two whitelisted intents above (inv /
    // look-around) already cover the question-shaped commands the
    // engine is ALLOWED to handle locally; everything else question-
    // shaped goes to the agent.
    if (isQuestion(raw, t)) return null;

    // ── NPC direct-address gate ──────────────────────────────────────
    // "frog, please don't" / "boatman, here's my coin" — when a player
    // begins their utterance with the name or alias of an NPC in this
    // room (with or without a comma), they're addressing the character.
    // Don't match local intents; let the agent voice the NPC's reply.
    if (startsWithNpcAddress(t, room)) return null;

    // EXAMINE X / look at X — matched BEFORE go so "look at the trees"
    // doesn't trigger a forest transition. Explicit examine verbs are
    // authoritative: if the verb fires but the target isn't here, we
    // tell the player rather than falling through.
    let m = t.match(/^(?:examine|inspect|look\s+(?:at|on)|study|x)\s+(?:the\s+)?(.+)$/);
    if (m) {
      const target = this.resolveObjectAlias(m[1], room);
      if (target) return { kind: 'examine', target };
      return { kind: 'examine', target: m[1] };  // not found — caller handles
    }

    // TAKE X — also authoritative when the verb is explicit. "steal"
    // and "swipe" are aliases for take so the player can verbalize the
    // moral charge of the action when the witch isn't looking.
    m = t.match(/^(?:take|pick\s+up|grab|get|pocket|steal|swipe|snatch)\s+(?:the\s+)?(.+)$/);
    if (m) {
      const target = this.resolveObjectAlias(m[1], room);
      if (target) return { kind: 'take', target };
      return { kind: 'take', target: m[1] };
    }

    // GO — match any exit phrase against the utterance, lenient.
    //   (a) full-phrase substring  ("go towards the trees" ⊃ "trees")
    //   (b) token-set match        ("walk into the river path" ⊇ {"river","path"})
    // We rank phrases by length so longer/more-specific phrases win
    // ("river path" beats "river" if both are present in the utterance).
    const ranked = room.exits.flatMap(e =>
      e.phrases.map(p => ({ exit: e, phrase: p.toLowerCase() }))
    ).sort((a, b) => b.phrase.length - a.phrase.length);
    for (const { exit, phrase } of ranked) {
      if (containsPhrase(t, phrase)) {
        return { kind: 'go', to: exit.to };
      }
      const pTokens = phrase.split(/\s+/).filter(w => w.length >= 3);
      if (pTokens.length > 0 && pTokens.every(w => tokens.has(w))) {
        return { kind: 'go', to: exit.to };
      }
    }

    // Motion-verb fallback for linear scenes — "continue", "let's go",
    // "walk forward", "the child moves on". If the player used a motion
    // word AND the room has exactly one forward exit, take it. This
    // unsticks the theater opening: only one way forward, "let's
    // continue" should advance.
    //
    // "Forward" here means: the exit is NOT exclusively a back/return/
    // leave passage. The previous filter rejected any exit with `back`
    // among its phrases — but exits like the swamp→crossroads passage
    // legitimately use both `back` AND `forward` as aliases (the same
    // door can be either depending on perspective). Now we require ALL
    // phrases of an exit to be back-meaning before excluding it.
    if (MOTION_VERBS.test(t)) {
      const forwardExits = room.exits.filter(e =>
        !e.phrases.every(p => /^(back|return|leave)$/i.test(p))
      );
      if (forwardExits.length === 1) {
        return { kind: 'go', to: forwardExits[0].to };
      }
    }

    // Bare name of a visible object → examine
    const bareTarget = this.resolveObjectAlias(t, room);
    if (bareTarget) return { kind: 'examine', target: bareTarget };

    return null;
  }

  private resolveObjectAlias(needle: string, room: Room): string | null {
    const n = needle.toLowerCase().trim();
    for (const o of room.objects) {
      if (o.hidden) continue;
      if (o.name.toLowerCase() === n) return o.id;
      if (o.aliases?.some(a => a.toLowerCase() === n)) return o.id;
    }
    // Also check NPCs (so "examine boatman" works)
    for (const npc of room.npcs) {
      if (npc.name.toLowerCase() === n || npc.id === n) return `__npc:${npc.id}`;
      if (npc.aliases?.some(a => a.toLowerCase() === n)) return `__npc:${npc.id}`;
    }
    return null;
  }

  // ─── Local action execution ──────────────────────────────────────

  private async executeLocalAction(action: ParsedAction): Promise<void> {
    const room = this.world.rooms[this.state.currentRoom];
    switch (action.kind) {
      case 'inventory': {
        const list = this.state.inventory.length
          ? `You're carrying: ${this.state.inventory
              .map(id => this.world.itemCatalog[id]?.label ?? id).join(', ')}.`
          : "Your pack is empty.";
        await this.speakLocal(list);
        return;
      }
      case 'look': {
        const prose = this.state.visited.size > 1 && room.returnProse
          ? room.returnProse
          : room.arrivalProse;
        await this.speakLocal(prose);
        return;
      }
      case 'examine': {
        if (action.target.startsWith('__npc:')) {
          const npc = room.npcs.find(n => n.id === action.target.slice(6));
          if (npc) await this.speakLocal(`You see ${npc.name}.`);
          return;
        }
        const obj = room.objects.find(o => o.id === action.target);
        if (!obj) { await this.speakLocal("You don't see that here."); return; }
        const prose = typeof obj.examineProse === 'function'
          ? obj.examineProse(this.state) : obj.examineProse;
        // If examining unhid an object (e.g., "examine bank" reveals wisp)
        // it'd be authored in examineProse; flags are agent-driven for now.
        await this.speakLocal(prose);

        // Narratively: examining the river bank reveals the wisp.
        if (obj.id === 'bank') {
          this.state.flags.wisp_seen = true;
          this.refreshInventory();
        }
        return;
      }
      case 'take': {
        const obj = room.objects.find(o => o.id === action.target);
        if (!obj) { await this.speakLocal("You don't see that to take."); return; }
        if (!obj.takeable) { await this.speakLocal("You can't take that."); return; }
        if (this.state.inventory.includes(obj.id)) {
          await this.speakLocal(`You already have the ${obj.name}.`); return;
        }
        this.state.inventory.push(obj.id);
        const prose = typeof obj.takeProse === 'function'
          ? obj.takeProse(this.state)
          : (obj.takeProse ?? `You take the ${obj.name}.`);
        await this.speakLocal(prose);
        this.refreshInventory();
        return;
      }
      case 'go': {
        // Special "__end__" sentinel means resolve ending.
        if (action.to === '__end__') {
          if (this.world.endingResolver) {
            const ending = this.world.endingResolver(this.state);
            if (ending) {
              this.endingFired = ending;
              return;
            }
          }
        }
        const exit = room.exits.find(e => e.to === action.to);
        if (!exit) return;
        if (exit.available && !exit.available(this.state)) {
          const blocked = typeof exit.blockedProse === 'function'
            ? exit.blockedProse(this.state)
            : (exit.blockedProse ?? "You can't go that way yet.");
          await this.speakLocal(blocked);
          return;
        }
        await this.enterRoom(exit.to);
        return;
      }
    }
  }

  /** Local prose path — voice every locally-resolved utterance through
   *  the same direct-TTS pipeline that narrates room arrivals. Originally
   *  this method only showed the subtitle (skipping TTS for "cheap"
   *  actions to save credits), but the resulting silent click on a
   *  hotspot read as broken — the narrator was supposed to be the
   *  player's only voice, and going mute on examine/take/inventory
   *  collapsed the illusion. Now every local action gets the narrator's
   *  voice. speakNarration already handles subtitle + music duck +
   *  fallback, so this stays a one-liner. */
  private async speakLocal(prose: string): Promise<void> {
    await this.speakNarration(prose);
  }

  // ─── Agent response handling ─────────────────────────────────────

  private handleAgentResponse(raw: string): void {
    const { prose, effects } = this.parseEffectTags(raw);
    const trimmed = prose.trim();
    if (trimmed) {
      // The Convai agent voices the prose itself through its own TTS
      // pipeline (the same TTS the runtime relies on for everything,
      // since runtime never uses an ElevenLabs API key directly).
      // Just sync the on-screen subtitle.
      this.showSubtitle(trimmed);
    }
    this.applyEffects(effects);
  }

  private parseEffectTags(text: string): { prose: string; effects: Effect[] } {
    const effects: Effect[] = [];
    const tagRegex = /<(flag|add_item|consume):([^>]+)>/g;
    let m: RegExpExecArray | null;
    while ((m = tagRegex.exec(text)) !== null) {
      const [, kind, payload] = m;
      if (kind === 'flag') {
        const eq = payload.indexOf('=');
        if (eq >= 0) {
          const name = payload.slice(0, eq).trim();
          const valueRaw = payload.slice(eq + 1).trim();
          const value = valueRaw === 'true' ? true
            : valueRaw === 'false' ? false
            : isFinite(Number(valueRaw)) ? Number(valueRaw)
            : valueRaw;
          effects.push({ kind: 'flag', name, value });
        } else {
          effects.push({ kind: 'flag', name: payload.trim(), value: true });
        }
      } else if (kind === 'add_item') {
        effects.push({ kind: 'add_item', id: payload.trim() });
      } else if (kind === 'consume') {
        effects.push({ kind: 'consume', id: payload.trim() });
      }
    }
    const prose = text.replace(tagRegex, '').replace(/\n+$/g, '').trim();
    return { prose, effects };
  }

  private applyEffects(effects: Effect[]): void {
    let invChanged = false;
    let autoCrossTriggered = false;
    let arenaTriggered = false;
    for (const e of effects) {
      if (e.kind === 'flag') {
        this.state.flags[e.name] = e.value as any;
        console.log('[parser] flag set:', e.name, '=', e.value);
        // Boatman accepted the coin → auto-cross the river. The player
        // already paid; no need to also guess a magic word like "cross"
        // or "across". Without this the river-room cross exit is gated
        // by paid_boatman but no movement command ever fires (NPC mode
        // routes player input to the agent, not the parser), so the
        // game soft-locks after the transaction. We schedule the
        // transition to run once any in-flight narration finishes.
        if (e.name === 'paid_boatman' && e.value === true &&
            this.state.currentRoom === 'river' && !autoCrossTriggered) {
          autoCrossTriggered = true;
          void this.autoCrossRiver();
        }
        // Forest crone refused → enter the arena (real-time fly-dodge
        // boss rush). Same auto-transition pattern as the river,
        // since the player just spoke into NPC mode and there's no
        // parser command path for "now go to the arena".
        if (e.name === 'declined_thread' && e.value === true &&
            this.state.currentRoom === 'forest' && !arenaTriggered) {
          arenaTriggered = true;
          void this.autoEnterArena();
        }
      } else if (e.kind === 'add_item') {
        if (!this.state.inventory.includes(e.id)) {
          this.state.inventory.push(e.id);
          invChanged = true;
          console.log('[parser] item added:', e.id);
        }
      } else if (e.kind === 'consume') {
        const i = this.state.inventory.indexOf(e.id);
        if (i >= 0) {
          this.state.inventory.splice(i, 1);
          invChanged = true;
          console.log('[parser] item consumed:', e.id);
        }
      }
    }
    if (invChanged) this.refreshInventory();
  }

  /** Apply per-room sprite image overrides for every VoiceKey that
   *  could be on stage. Overridden sprites swap to the room's declared
   *  art; non-overridden sprites are reset to their CHARACTER_LAYOUT
   *  default — this is what keeps a prior room's override from
   *  bleeding into the next room. */
  /** Collect every sprite image URL the world might use (CHARACTER_LAYOUT
   *  defaults + every per-room override main+alt) and pre-decode them
   *  into the PaperSprite cache. Means every applySpriteOverrides call
   *  hits the cache and assigns synchronously — no flicker on first
   *  visit to any room. */
  private preloadAllSpriteImages(): Promise<void> {
    const urls = new Set<string>();
    for (const layout of Object.values(CHARACTER_LAYOUT)) {
      if (layout.imageUrl) urls.add(layout.imageUrl);
      if (layout.altImageUrl) urls.add(layout.altImageUrl);
    }
    for (const room of Object.values(this.world.rooms)) {
      if (!room.spriteImageOverrides) continue;
      for (const override of Object.values(room.spriteImageOverrides)) {
        if (override?.main) urls.add(override.main);
        if (override?.alt) urls.add(override.alt);
      }
    }
    return PaperSprite.preload([...urls]);
  }

  /** Phase 1 of the override pipeline: textures only. Runs BEFORE
   *  snapVisible.
   *
   *  Bulletproof path: resolves every URL to a fully-decoded
   *  HTMLImageElement via PaperSprite.getCached, then synchronously
   *  installs them into the sprite's main/alt slots via setImagesSync.
   *  Zero async, zero version races, zero alt-slot timing weirdness —
   *  the previous URL-based path had a subtle bug where the swamp
   *  animation timer could fire between override and clear, leaving
   *  altImage holding frog_back while the witchHouse override expected
   *  altImage to be null. Since this assignment is fully synchronous,
   *  there's nowhere for stale state to hide.
   *
   *  Cache miss falls back to the async URL path so unpreloaded images
   *  still load eventually. The preload step in run() catches every
   *  override URL at boot, so cache misses are rare. */
  private applySpriteOverrides(room: Room) {
    console.log(`[parser] applySpriteOverrides for room "${room.id}"`);
    const voiceKeys = Object.keys(CHARACTER_LAYOUT) as VoiceKey[];
    for (const key of voiceKeys) {
      const sprite = this.stage.sprite(key);
      if (!sprite) continue;
      const override = room.spriteImageOverrides?.[key];
      const layout = CHARACTER_LAYOUT[key];
      const mainUrl = override ? override.main : layout.imageUrl ?? null;
      // Default restoration must include the layout's altImageUrl
      // (e.g. the frog slot has both front + back baked in by default).
      // Without this, every non-override room would null out the
      // frog's alt slot and break its animation.
      const altUrl = override ? override.alt : layout.altImageUrl ?? null;

      // ── FORCE-RESET everything before we touch slots ─────────────
      // Belt-and-suspenders: explicitly null both image slots and
      // pin usingAlt to false BEFORE assigning anything new. If
      // there's any stale state (a stray animation tick, a stale
      // async load, browser HMR weirdness), this wipes it.
      sprite.setImagesSync(null, null);

      // Try the sync cache path first.
      const mainImg = (mainUrl && typeof mainUrl === 'string') ? PaperSprite.getCached(mainUrl) : null;
      const altImg = (altUrl && typeof altUrl === 'string') ? PaperSprite.getCached(altUrl) : null;

      const mainHit = !mainUrl || mainImg !== null;
      const altHit = !altUrl || altImg !== null;

      if (key === 'crone' || override) {
        console.log(`[parser]   ${key}: mainUrl=${mainUrl}, altUrl=${altUrl}, mainHit=${mainHit}, altHit=${altHit}, usingAlt=after-clear=${false}`);
      }

      if (mainHit && altHit) {
        // Fully cached — synchronous install. No race possible.
        sprite.setImagesSync(mainImg, altImg);
        if (key === 'crone' || override) {
          console.log(`[parser]   ${key}: setImagesSync(${mainImg ? 'IMG' : 'null'}, ${altImg ? 'IMG' : 'null'}) — main src=${mainImg?.src}`);
        }
      } else {
        // Cache miss on at least one slot — fall back to async URL load.
        console.warn(`[parser] sprite cache miss for ${key}: main=${mainUrl} (hit=${mainHit}), alt=${altUrl} (hit=${altHit})`);
        sprite.setImages({ main: mainUrl, alt: altUrl });
        sprite.setUseAlt(false);
      }
    }
    this.spriteAnimUseAlt = false;
  }

  /** Phase 2 of the override pipeline: mesh geometry only.
   *  Runs AFTER snapVisible, because snapVisible itself stomps
   *  mesh.scale to (1,1,1) — if we set the scale before, our portrait
   *  override gets reverted to the default banner geometry the
   *  instant the sprite is snapped visible.
   *
   *  Per-room scale narrows the wide banner-shaped quad (5.0×2.3 for
   *  `crone`, sized for the angel banner) into a portrait quad that
   *  matches the witch / frog cutouts. Without this, the cutout
   *  letterboxes inside the banner and the surrounding area renders
   *  the wooden bar + strings of the angel default. */
  private applySpriteScales(room: Room) {
    const voiceKeys = Object.keys(CHARACTER_LAYOUT) as VoiceKey[];
    for (const key of voiceKeys) {
      const sprite = this.stage.sprite(key);
      if (!sprite) continue;
      const override = room.spriteImageOverrides?.[key];
      if (override?.scale) {
        const sx = override.scale.x ?? 1;
        const sy = override.scale.y ?? 1;
        sprite.mesh.scale.set(sx, sy, 1);
      } else {
        sprite.mesh.scale.set(1, 1, 1);
      }
    }
  }

  /** Stop the previous room's sprite-frame animation (if any) and start
   *  this room's, if `spriteAnim` is set. The timer toggles between
   *  the sprite's main and alt images on the configured interval — used
   *  by the swamp frog (front ↔ back cutouts every ~1.2s). */
  private restartSpriteAnim(room: Room) {
    if (this.spriteAnimTimer !== null) {
      clearInterval(this.spriteAnimTimer);
      this.spriteAnimTimer = null;
    }
    const anim = room.spriteAnim;
    if (!anim) return;
    this.spriteAnimUseAlt = false;
    this.spriteAnimTimer = window.setInterval(() => {
      const sprite = this.stage.sprite(anim.who);
      if (!sprite) return;
      this.spriteAnimUseAlt = !this.spriteAnimUseAlt;
      sprite.setUseAlt(this.spriteAnimUseAlt);
    }, anim.intervalMs);
  }

  /** Boatman has been paid → exit conversation, transition to the far
   *  bank. Called once when paid_boatman flips to true via an effect
   *  tag in the agent's response. Does NOT interrupt the boatman's
   *  spoken acceptance — waits for narrationInFlight first so the
   *  player hears the line in full before the curtain falls. */
  private async autoCrossRiver(): Promise<void> {
    console.log('[parser] auto-cross river: waiting for narration to settle');
    if (this.narrationInFlight) {
      try { await this.narrationInFlight; } catch { /* */ }
    }
    // A small breath after the boatman's last word so the curtain
    // close doesn't step on the audio tail.
    await wait(450);
    if (this.npcMode) {
      console.log('[parser] auto-cross river: exiting NPC mode');
      await this.exitNpcMode();
    }
    console.log('[parser] auto-cross river: enterRoom(hollow)');
    await this.enterRoom('hollow');
  }

  /** Forest crone has been refused → transition to the arena room.
   *  Mirrors autoCrossRiver: waits for the crone's spoken refusal to
   *  finish, then closes NPC mode and enters arena. */
  private async autoEnterArena(): Promise<void> {
    console.log('[parser] auto-enter arena: waiting for narration to settle');
    if (this.narrationInFlight) {
      try { await this.narrationInFlight; } catch { /* */ }
    }
    await wait(450);
    if (this.npcMode) {
      console.log('[parser] auto-enter arena: exiting NPC mode');
      await this.exitNpcMode();
    }
    console.log('[parser] auto-enter arena: enterRoom(arena)');
    await this.enterRoom('arena');
  }

  /** Arena room handler — runs the real-time fly-dodge mini-game
   *  instead of the parser pipeline. Hides parser UI, swaps to a
   *  dark backdrop, runs ArenaRunner, then either transitions to
   *  exitOnArenaWin (survived) or lets the failure ending fire
   *  through the existing pipeline (lives hit 0). */
  private async runArenaRoom(room: Room): Promise<void> {
    console.log(`[parser] entering arena room: ${room.id}`);

    // Tear down the previous room's session so the agent doesn't
    // try to chime in during a real-time scene.
    if (this.session) {
      try { await this.session.endSession(); } catch { /* */ }
      this.session = null;
    }
    this.convaiAlive = false;
    this.cancelResponseWatchdog();
    this.convaiPendingContextual.length = 0;

    this.state.cameFrom = this.state.currentRoom;
    this.state.currentRoom = room.id;
    this.state.visited.add(room.id);

    // Hide parser overlays — the arena owns the screen.
    this.hideSubtitle();
    this.hotspotsUI.clear();
    this.hotspotsUI.hide();
    this.hideHint();

    // Curtain swap to the arena's empty backdrop. We can't use
    // applySpriteOverrides etc. because there are no sprites; just
    // hide everything cleanly.
    if (this.stage.isCurtainOpen()) {
      void this.audio.playSfx('curtain_close', { volume: 0.7 });
      await this.stage.curtainClose();
    }
    this.stage.clearStage();
    this.stage.setBackdrop(room.backdrop);
    if (room.music === null) this.audio.stopMusic();
    await wait(150);
    void this.audio.playSfx('curtain_open', { volume: 0.7 });
    await this.stage.curtainOpen();

    // Hide ceiling props for the arena (treat as interior).
    const ceilingProps = document.getElementById('ceiling-props');
    if (ceilingProps) {
      ceilingProps.classList.remove('visible');
      ceilingProps.classList.add('hidden');
    }

    // Run the arena. Engine reference exposes loseLife() and a lives
    // getter — that's all the runner needs to drive damage. Closures
    // over `this` (via an outer alias so the getter can read state).
    track('parser_arena_started', { worldId: this.world.id });
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    const arena = new ArenaRunner({
      loseLife: () => self.loseLife(),
      get lives() { return self.state.lives; },
    });
    const won = await arena.run();

    track('parser_arena_finished', { worldId: this.world.id, won, lives: this.state.lives });

    // Restore parser overlays for whatever scene comes next.
    this.hotspotsUI.show();

    if (won) {
      this.state.flags.survived_arena = true;
      const next = room.exitOnArenaWin ?? 'hollow';
      console.log(`[parser] arena: won, entering ${next}`);
      await this.enterRoom(next);
    } else {
      // Player ran out of lives. The failure ending was already
      // queued by loseLife() when state.lives hit 0; nothing left to
      // do here — the run loop will see endingFired and bail.
      console.log('[parser] arena: lost (lives = 0). failure ending in flight.');
    }
  }

  private refreshInventory() {
    this.inventoryUI.set(this.state.inventory);
  }

  /** Rebuild the on-stage hotspot overlays for the current room — one
   *  per object whose authored `propKey` matches a visible prop. Skips
   *  hidden objects (e.g. things gated behind another examine). */
  private refreshHotspots(room: Room) {
    // Object hotspots (existing behaviour).
    const objectItems: import('./hotspots.ts').HotspotItem[] = room.objects
      .filter(o => o.propKey && !o.hidden)
      .map(o => ({ id: o.id, label: o.name, propKey: o.propKey! }));
    // NPC hotspots — track the on-stage character sprite so hover
    // outlines and clicks fall on the figure itself, not a prop. The
    // engine namespaces NPC ids with `__npc:` so onHotspotClick can
    // tell them apart from object ids.
    const npcItems: import('./hotspots.ts').HotspotItem[] = room.npcs.map(n => ({
      kind: 'character' as const,
      id: `__npc:${n.id}`,
      label: n.name,
      spriteKey: n.spriteKey,
    }));
    this.hotspotsUI.set([...objectItems, ...npcItems]);
  }

  // ─── V key — push-to-talk (uses the SDK mic) ─────────────────────

  private installVKey() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }
  private removeVKey() {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
  }
  private onKeyDown = async (e: KeyboardEvent) => {
    if (e.code !== 'KeyV' || this.vHeld || e.repeat) return;
    this.vHeld = true;
    // Player wants to speak — interrupt anything we're voicing right
    // now and cancel anything queued behind it. They don't want to
    // wait for the line to finish; they want to be heard.
    this.interruptNarration();
    // Player is starting a new turn — cancel any pending watchdog
    // from the previous turn so a stale fallback doesn't fire while
    // they're mid-question.
    this.cancelResponseWatchdog();
    this.updateHint();
    // If the socket idled out / was kicked, reconnect BEFORE unmuting.
    // Otherwise the player's audio goes nowhere and the agent never
    // hears the question. Fast reconnect (<1s typically) is much less
    // bad than the silent-failure mode.
    const ok = await this.ensureConvaiAlive();
    // Player may have released V during the reconnect await — only
    // open the mic if they're still holding the key.
    if (ok && this.vHeld) {
      this.safeSetMicMuted(false);
    }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code !== 'KeyV' || !this.vHeld) return;
    this.vHeld = false;
    this.safeSetMicMuted(true);
    this.updateHint();
    // DO NOT start the response watchdog here. With the turn_v2 model
    // the server detects end-of-thought from the audio itself, so the
    // full transcript+agent-response round-trip can complete WHILE the
    // player is still holding V. Releasing V after that would start a
    // phantom watchdog with no reply coming, firing a stale fallback
    // ~15s later. The watchdog is now started exclusively in onMessage
    // when a user transcript actually arrives — that's the only point
    // where we definitely know an agent reply is expected.
  };

  // ─── UI helpers ───────────────────────────────────────────────────

  private showSubtitle(text: string) {
    this.subtitleEl.textContent = text;
    this.subtitleEl.classList.remove('hidden');
    void this.subtitleEl.offsetWidth;
    this.subtitleEl.classList.add('visible');
  }
  private hideSubtitle() {
    this.subtitleEl.classList.remove('visible');
  }
  private showHint() {
    if (this.hintEl) {
      this.hintEl.classList.remove('hidden');
      void this.hintEl.offsetWidth;
      this.hintEl.classList.add('active');
    }
  }
  private hideHint() {
    if (this.hintEl) {
      this.hintEl.classList.remove('active');
      setTimeout(() => this.hintEl?.classList.add('hidden'), 280);
    }
  }
  private updateHint() {
    const sub = this.hintEl?.querySelector('.vph-sub') as HTMLElement | null;
    if (sub) sub.textContent = this.vHeld
      ? '🎙 listening — release V to send'
      : 'hold V and speak — try "go to the river" or "talk to the boatman"';
  }
}

// ─── Internal helpers ────────────────────────────────────────────────

type Effect =
  | { kind: 'flag'; name: string; value: unknown }
  | { kind: 'add_item'; id: string }
  | { kind: 'consume'; id: string };

function wait(ms: number): Promise<void> {
  return new Promise(r => setTimeout(r, ms));
}

/** Substring match that respects word boundaries — so "back" doesn't
 *  match "backpack", but "go to the river" does match "river". */
function containsPhrase(haystack: string, needle: string): boolean {
  if (!needle) return false;
  const re = new RegExp(`(^|\\s)${needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`);
  return re.test(haystack);
}

/** Heuristic: is the player's utterance an information-seeking question
 *  rather than a command? Used to bail out of local intent matching so
 *  the agent can answer instead of the engine accidentally turning the
 *  question into navigation or take/examine.
 *
 *  Three signals, any of which is enough:
 *    1. Raw input contains a literal '?' (set by Convai STT for some
 *       prosodies; rare but cheap to check).
 *    2. Normalized input starts with a wh-word (where/what/who/...).
 *    3. Normalized input starts with a yes/no auxiliary ("is the…",
 *       "can I…", "should we…") followed by a likely subject. The
 *       subject check is what keeps "do" in "do it" from being misread
 *       as a question — we require the auxiliary to be followed by
 *       a pronoun or "the".
 *    4. Normalized input begins with explicit info-seeking phrases
 *       like "tell me", "tell us", "describe the options". */
const WH_WORDS = /^(where|what|who|whom|whose|why|when|which|how)\b/;
const QUESTION_AUX = /^(is|are|am|was|were|do|does|did|can|could|should|would|will|may|might|shall|has|have|had)\s+(i|we|you|the|it|that|this|he|she|they)\b/;
const QUESTION_PHRASES = /^(tell\s+me|tell\s+us|let\s+me\s+know|describe\s+(?:the\s+)?(?:options|exits|paths|choices))\b/;

function isQuestion(raw: string, normalized: string): boolean {
  if (raw.includes('?')) return true;
  if (WH_WORDS.test(normalized)) return true;
  if (QUESTION_AUX.test(normalized)) return true;
  if (QUESTION_PHRASES.test(normalized)) return true;
  return false;
}

/** Does the utterance begin with a direct address to one of the room's
 *  NPCs? E.g. "frog, please…", "boatman: take this coin", "crone — what
 *  is your name". When yes, the player is talking TO the character;
 *  the engine should not parse the rest as a command. The agent (which
 *  has the NPC roster + personas in its prompt) handles the reply. */
function startsWithNpcAddress(t: string, room: Room): boolean {
  // Collect every alias and full name of every NPC in this room, plus
  // the bare id (e.g. "frog"). Strip any leading article.
  const aliases: string[] = [];
  for (const npc of room.npcs) {
    aliases.push(npc.id.toLowerCase());
    aliases.push(npc.name.toLowerCase().replace(/^the\s+/, ''));
    if (npc.aliases) for (const a of npc.aliases) aliases.push(a.toLowerCase());
  }
  // Sort longest-first so multi-word addresses like "swamp witch" win
  // over the bare "witch".
  aliases.sort((a, b) => b.length - a.length);
  for (const a of aliases) {
    if (!a) continue;
    // Match: the alias at the start of the utterance, followed by EOL,
    // a punctuation-y separator (already normalized to whitespace by the
    // caller), or a verb that suggests addressing ("please", "tell",
    // "wait", or any vocative pause). Bare alias-only is handled by the
    // existing bareTarget path, NOT here, so a single word "frog"
    // doesn't prematurely block examine on the NPC.
    const re = new RegExp(`^${a.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s+(?!is|are|was|were|seems|looks|stands|sits|watches)`);
    if (re.test(t)) return true;
  }
  return false;
}

/** Words that imply the player wants to advance, even without naming a
 *  specific destination. Used for the linear-scene fallback so the
 *  opening theater can advance on "continue", "let's go", "walk
 *  forward", "the child moves on", etc. */
const MOTION_VERBS = /\b(continue|continues|continuing|proceed|proceeds|advance|advances|forward|onward|onwards|onto|ahead|next|begin|begins|beginning|start|starts|starting|go|goes|going|gone|walk|walks|walking|run|runs|running|head|heading|headed|move|moves|moving|moved|travel|travels|step|steps|stepping|march|marches|venture|ventures|enter|enters|entering|approach|approaches|approaching|cross|crosses|crossing|follow|follows|following)\b/;

/** Phrases that exit NPC click-to-talk mode. Anchored to the start so
 *  longer utterances ("back to the witch's hut please") still trigger
 *  the exit cleanly — the player is asking to step away from this
 *  character. */
const NPC_MODE_EXIT = /^(back|leave|stop|nevermind|never\s+mind|goodbye|bye|enough|step\s+back|away)\b/i;

/** Try to match the given (already-normalized) utterance against any of
 *  the current room's exit phrases. Returns the destination room id on
 *  match, null otherwise. Mirrors the GO logic in parseLocalIntent —
 *  full-phrase substring OR token-set match — but is exposed here as a
 *  standalone helper so NPC mode can escape into a movement command
 *  without going through parseLocalIntent (which it short-circuits). */
function matchRoomExit(t: string, room: Room): string | null {
  if (!room) return null;
  const tokens = new Set(t.split(' ').filter(Boolean));
  const ranked = room.exits.flatMap(e =>
    e.phrases.map(p => ({ exit: e, phrase: p.toLowerCase() }))
  ).sort((a, b) => b.phrase.length - a.phrase.length);
  for (const { exit, phrase } of ranked) {
    if (containsPhrase(t, phrase)) return exit.to;
    const pTokens = phrase.split(/\s+/).filter(w => w.length >= 3);
    if (pTokens.length > 0 && pTokens.every(w => tokens.has(w))) return exit.to;
  }
  return null;
}
